import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckIcon,
  XMarkIcon,
  StarIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Pricing: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small CA firms getting started',
      monthlyPrice: 2999,
      yearlyPrice: 29990,
      features: [
        'Up to 100 invoices/month',
        'AI-powered OCR extraction',
        'Basic client portal',
        'Email support',
        'Data export (CSV/Excel)',
        'Basic analytics dashboard',
      ],
      limitations: [
        'No advanced integrations',
        'Limited customization',
        'No priority support',
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Professional',
      description: 'Ideal for growing CA practices',
      monthlyPrice: 5999,
      yearlyPrice: 59990,
      features: [
        'Up to 500 invoices/month',
        'Advanced AI with 99.5% accuracy',
        'Branded client portals',
        'Priority email & chat support',
        'Advanced analytics & reporting',
        'API integrations',
        'Custom workflows',
        'Multi-user access (up to 5)',
        'Automated approval workflows',
        'Data backup & recovery',
      ],
      limitations: [
        'Limited to 5 users',
        'No phone support',
      ],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      name: 'Enterprise',
      description: 'For large CA firms with high volume',
      monthlyPrice: 12999,
      yearlyPrice: 129990,
      features: [
        'Unlimited invoices',
        'Premium AI with custom training',
        'White-label client portals',
        '24/7 phone & priority support',
        'Advanced analytics & BI',
        'Full API access',
        'Custom integrations',
        'Unlimited users',
        'Advanced security & compliance',
        'Dedicated account manager',
        'Custom SLA',
        'On-premise deployment option',
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const features = [
    {
      category: 'Core Features',
      items: [
        { name: 'AI-Powered OCR', starter: true, professional: true, enterprise: true },
        { name: 'Invoice Data Extraction', starter: true, professional: true, enterprise: true },
        { name: 'Client Upload Portal', starter: true, professional: true, enterprise: true },
        { name: 'Data Export', starter: true, professional: true, enterprise: true },
        { name: 'Mobile App Access', starter: false, professional: true, enterprise: true },
        { name: 'Advanced AI Training', starter: false, professional: false, enterprise: true },
      ]
    },
    {
      category: 'Analytics & Reporting',
      items: [
        { name: 'Basic Dashboard', starter: true, professional: true, enterprise: true },
        { name: 'Advanced Analytics', starter: false, professional: true, enterprise: true },
        { name: 'Custom Reports', starter: false, professional: true, enterprise: true },
        { name: 'Business Intelligence', starter: false, professional: false, enterprise: true },
      ]
    },
    {
      category: 'Support & Security',
      items: [
        { name: 'Email Support', starter: true, professional: true, enterprise: true },
        { name: 'Priority Support', starter: false, professional: true, enterprise: true },
        { name: '24/7 Phone Support', starter: false, professional: false, enterprise: true },
        { name: 'Dedicated Account Manager', starter: false, professional: false, enterprise: true },
        { name: 'SOC 2 Compliance', starter: false, professional: true, enterprise: true },
        { name: 'Custom SLA', starter: false, professional: false, enterprise: true },
      ]
    }
  ];

  const addOns = [
    {
      name: 'Additional Users',
      description: 'Add more team members to your account',
      price: 499,
      per: 'user/month'
    },
    {
      name: 'Extra Invoice Processing',
      description: 'Process more invoices beyond your plan limit',
      price: 5,
      per: 'invoice'
    },
    {
      name: 'Custom Integration',
      description: 'Connect with your existing accounting software',
      price: 9999,
      per: 'one-time setup'
    },
    {
      name: 'White-label Branding',
      description: 'Customize the platform with your branding',
      price: 2999,
      per: 'month'
    }
  ];

  const savings = billingCycle === 'yearly' ? 20 : 0;

  return (
    <div className="bg-white py-12">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Choose the perfect plan for your CA firm. All plans include a 14-day free trial with no setup fees.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4">
            <span className={`text-lg ${billingCycle === 'monthly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-lg ${billingCycle === 'yearly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <span className="bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                Save 20%
              </span>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl border-2 p-8 ${
                plan.popular 
                  ? 'border-indigo-500 pricing-glow' 
                  : 'border-gray-200 hover:border-gray-300'
              } transition-all duration-300`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <StarIcon className="h-4 w-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">
                    ₹{billingCycle === 'monthly' ? plan.monthlyPrice.toLocaleString() : Math.floor(plan.yearlyPrice / 12).toLocaleString()}
                  </span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="text-sm text-green-600 font-medium">
                    Save ₹{((plan.monthlyPrice * 12) - plan.yearlyPrice).toLocaleString()} per year
                  </p>
                )}
              </div>

              <div className="space-y-4 mb-8">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-start space-x-3">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
                {plan.limitations.map((limitation) => (
                  <div key={limitation} className="flex items-start space-x-3">
                    <XMarkIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-500">{limitation}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105'
                    : 'border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white'
                }`}
              >
                {plan.cta}
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Detailed Feature Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-200 px-6 py-4 text-left font-semibold text-gray-900">
                    Features
                  </th>
                  <th className="border border-gray-200 px-6 py-4 text-center font-semibold text-gray-900">
                    Starter
                  </th>
                  <th className="border border-gray-200 px-6 py-4 text-center font-semibold text-gray-900">
                    Professional
                  </th>
                  <th className="border border-gray-200 px-6 py-4 text-center font-semibold text-gray-900">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map((category) => (
                  <React.Fragment key={category.category}>
                    <tr className="bg-gray-100">
                      <td colSpan={4} className="border border-gray-200 px-6 py-3 font-semibold text-gray-900">
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item) => (
                      <tr key={item.name} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-6 py-4 text-gray-700">
                          {item.name}
                        </td>
                        <td className="border border-gray-200 px-6 py-4 text-center">
                          {item.starter ? (
                            <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <XMarkIcon className="h-5 w-5 text-gray-400 mx-auto" />
                          )}
                        </td>
                        <td className="border border-gray-200 px-6 py-4 text-center">
                          {item.professional ? (
                            <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <XMarkIcon className="h-5 w-5 text-gray-400 mx-auto" />
                          )}
                        </td>
                        <td className="border border-gray-200 px-6 py-4 text-center">
                          {item.enterprise ? (
                            <CheckIcon className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <XMarkIcon className="h-5 w-5 text-gray-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add-ons */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Optional Add-ons
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addOns.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="font-semibold text-gray-900 mb-2">{addon.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{addon.description}</p>
                <div className="text-2xl font-bold text-indigo-600">
                  ₹{addon.price.toLocaleString()}
                  <span className="text-sm text-gray-500 font-normal">/{addon.per}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: 'Can I change my plan anytime?',
                answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.'
              },
              {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major credit cards, debit cards, UPI, and bank transfers. All payments are processed securely.'
              },
              {
                question: 'Is there a setup fee?',
                answer: 'No, there are no setup fees for any of our plans. You only pay the monthly or yearly subscription fee.'
              },
              {
                question: 'What happens if I exceed my invoice limit?',
                answer: 'You can either upgrade your plan or purchase additional invoice processing capacity as needed.'
              },
              {
                question: 'Do you offer refunds?',
                answer: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied, we\'ll refund your payment.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join 500+ CA firms already using AICaFriend to streamline their practice
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-indigo-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-indigo-600 transition-all duration-200"
            >
              Schedule Demo
            </Link>
          </div>
          <p className="mt-4 text-sm text-indigo-200">
            14-day free trial • No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;